import axios from "axios";
import { installPlatformInterceptors } from "./apiPlatform";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

// Callback invocado quando o refresh falha em modo platform. O AuthProvider
// registra o handler no boot para limpar o estado e voltar ao login sem
// manipular cookies diretamente pelo JavaScript.
let onPlatformUnauthorized = () => {};
export function setPlatformUnauthorizedHandler(fn) {
  onPlatformUnauthorized = typeof fn === "function" ? fn : (() => {});
}

installPlatformInterceptors(api, {
  onUnauthorized: () => onPlatformUnauthorized(),
});

export function formatApiError(err) {
  const detail = err?.response?.data?.detail;
  if (detail == null) return err?.message || "Erro inesperado.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  }
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export const ROLE_LABELS = {
  admin: "Administrador",
  manager: "Gerente",
  member: "Membro",
  viewer: "Visualizador",
};

export const STATUS_LABELS = {
  active: "Ativo",
  suspended: "Suspenso",
  pending: "Convite pendente",
  invite_expired: "Convite expirado",
  archived: "Arquivado",
};

export const PROJECT_STATUS_LABELS = { active: "Ativo", archived: "Arquivado" };
export const CLASSIFICATION_LABELS = { E: "Essencial", D: "Desejável", O: "Opcional", Q: "Questionável" };
export const PRIORITY_LABELS = { low: "Baixa", medium: "Média", high: "Alta", urgent: "Urgente" };
export const VISIBILITY_LABELS = { project: "Projeto", restricted: "Restrita", confidential: "Confidencial" };
export const TASK_STATUS_LABELS = { open: "Aberta", done: "Concluída", cancelled: "Cancelada" };
export const SPRINT_STATUS_LABELS = { planned: "Planejada", active: "Em andamento", completed: "Concluída", cancelled: "Cancelada", archived: "Arquivada" };

export const CLASS_STYLE = { E: "bg-red-50 text-red-700", D: "bg-amber-50 text-amber-700", O: "bg-slate-100 text-slate-600", Q: "bg-violet-50 text-violet-700" };
export const PRIO_STYLE = { low: "bg-slate-100 text-slate-600", medium: "bg-blue-50 text-blue-700", high: "bg-orange-50 text-orange-700", urgent: "bg-red-50 text-red-700" };
export const STATUS_STYLE = { open: "bg-emerald-50 text-emerald-700", done: "bg-slate-100 text-slate-600", cancelled: "bg-rose-50 text-rose-700" };
export const SPRINT_STATUS_STYLE = { planned: "bg-slate-100 text-slate-600", active: "bg-emerald-50 text-emerald-700", completed: "bg-blue-50 text-blue-700", cancelled: "bg-rose-50 text-rose-700", archived: "bg-slate-100 text-slate-500" };

// Fixed Kanban columns. id == key. Optional "blocked" column (Impedimento/Problema).
export const COLUMN_KEY_LABELS = { todo: "A fazer", doing: "Em andamento", review: "Em revisão", done: "Concluído", blocked: "Impedimento/Problema" };
export const IMPEDIMENT_KEY = "blocked";
// Operational status derived from the board column (single source of truth while on Kanban).
export const BOARD_STATUS_LABELS = { todo: "A fazer", doing: "Em andamento", review: "Em revisão", done: "Concluído" };

export const PROJECT_LIFECYCLE_LABELS = { planning: "Planejamento", in_progress: "Em andamento", paused: "Pausado", completed: "Concluído" };
export const PROJECT_LIFECYCLE_STYLE = { planning: "bg-amber-50 text-amber-700", in_progress: "bg-emerald-50 text-emerald-700", paused: "bg-slate-100 text-slate-600", completed: "bg-blue-50 text-blue-700" };

// Recurring tasks (Bloco 5)
export const REC_FREQ_LABELS = { daily: "Diária", weekly: "Semanal", monthly: "Mensal", yearly: "Anual" };
export const REC_STATUS_LABELS = { active: "Ativa", paused: "Pausada", ended: "Encerrada", needs_review: "Revisão necessária" };
export const REC_STATUS_STYLE = {
  active: "bg-emerald-50 text-emerald-700",
  paused: "bg-amber-50 text-amber-700",
  ended: "bg-slate-100 text-slate-500",
  needs_review: "bg-rose-50 text-rose-700",
};
export const REC_OVERLAP_LABELS = { allow: "Permitir simultâneas", no_overlap: "Não gerar com ocorrência aberta" };
export const WEEKDAYS_PT = [
  { v: 0, label: "Seg" }, { v: 1, label: "Ter" }, { v: 2, label: "Qua" },
  { v: 3, label: "Qui" }, { v: 4, label: "Sex" }, { v: 5, label: "Sáb" }, { v: 6, label: "Dom" },
];

// Returns the route + ?task= that opens a task in its REAL operational location.
export function taskHref(t) {
  const base = `/projetos/${t.project_id}`;
  const loc = t.location || (t.archived ? "archived" : (t.in_kanban ? "kanban" : "backlog"));
  if (loc === "archived") return `${base}/arquivadas?task=${t.id}`;
  if (loc === "kanban" || loc === "impediment") return `${base}/kanban?task=${t.id}`;
  return `${base}/backlog?task=${t.id}`;
}
